import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { PageHeader } from '../../components/layout/PageHeader';
import { AssetStatCards } from '../../components/assets/AssetStatCards';
import { AssetTable } from '../../components/assets/AssetTable';
import { AssetFilters } from '../../components/assets/AssetFilters';
import { AssetFormDialog } from '../../components/assets/AssetFormDialog';
import { AssetDeleteDialog } from '../../components/assets/AssetDeleteDialog';
import { useAssetIndexStore } from '../../stores/asset-index';
import { usePresenceStore } from '../../stores/presence';
import { useUploadToQueue } from '../../stores/queue';
import { useAuthStore } from '../../stores/auth';
import { toast } from 'sonner';
import { Plus, Filter } from 'lucide-react';
import type { AssetListFilter } from '../../types/api';

interface AssetsPageProps {
  defaultFilter?: Partial<AssetListFilter>;
  showStats?: boolean;
  pageTitle?: string;
  pageDescription?: string;
  breadcrumbs?: Array<{ label: string; href?: string }>;
}

export function AssetsPage({
  defaultFilter = {},
  showStats = false,
  pageTitle = 'Assets',
  pageDescription = 'Manage your asset inventory',
  breadcrumbs = [{ label: 'Assets' }],
}: AssetsPageProps) {
  const user = useAuthStore(s => s.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [editAssetId, setEditAssetId] = useState<number | null>(null);
  const [deleteAssetId, setDeleteAssetId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadToQueue();

  const [filter, setFilter] = useState<AssetListFilter>(() => {
    const urlFilter: AssetListFilter = { ...defaultFilter };

    if (searchParams.get('category_id')) {
      urlFilter.category_id = parseInt(searchParams.get('category_id')!, 10);
    }
    if (searchParams.get('subcategory_id')) {
      urlFilter.subcategory_id = parseInt(searchParams.get('subcategory_id')!, 10);
    }
    if (searchParams.get('lifecycle_status')) {
      urlFilter.lifecycle_status = searchParams.get('lifecycle_status') as any;
    }
    if (searchParams.get('disposition_status')) {
      urlFilter.disposition_status = searchParams.get('disposition_status') as any;
    }
    if (searchParams.get('street_address')) {
      urlFilter.street_address = searchParams.get('street_address') as any;
    }
    if (searchParams.get('search')) {
      urlFilter.search = searchParams.get('search')!;
    }

    return urlFilter;
  });

  const storeSetFilter = useAssetIndexStore(s => s.setFilter);
  useEffect(() => { storeSetFilter(filter); }, [filter, storeSetFilter]);
  useAssetIndexStore.live();
  const assets = useAssetIndexStore(s => s.assets);
  const total = useAssetIndexStore(s => s.total);
  const isLoading = useAssetIndexStore(s => s._loading);
  const data = isLoading ? undefined : { assets, total };

  usePresenceStore.live({ entity: "asset" });
  const presence = usePresenceStore(s => s.presence);

  useEffect(() => {
    const params = new URLSearchParams();

    if (filter.category_id) params.set('category_id', filter.category_id.toString());
    if (filter.subcategory_id) params.set('subcategory_id', filter.subcategory_id.toString());
    if (filter.lifecycle_status) params.set('lifecycle_status', filter.lifecycle_status);
    if (filter.disposition_status) params.set('disposition_status', filter.disposition_status);
    if (filter.street_address) params.set('street_address', filter.street_address);
    if (filter.search) params.set('search', filter.search);

    setSearchParams(params, { replace: true });
  }, [filter, setSearchParams]);

  const handleFiltersChange = (newFilters: AssetListFilter) => {
    setFilter(newFilters);
  };

  const canCreate = user?.role === 'Admin' || user?.role === 'Editor';

  const handleNewAsset = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter((f) =>
      f.type.startsWith('image/')
    );
    if (files.length === 0) return;

    const batchId = crypto.randomUUID();
    toast.info(`Uploading ${files.length} image${files.length > 1 ? 's' : ''}...`);
    upload.mutate({ files, batchId })
      .then(() => toast.success('Asset queued for processing'))
      .catch((error: Error) => toast.error(`Upload failed: ${error.message}`));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const editRow = editAssetId
    ? data?.assets.find((a) => a.asset_id === editAssetId)
    : undefined;

  const deleteRow = deleteAssetId
    ? data?.assets.find((a) => a.asset_id === deleteAssetId)
    : undefined;

  return (
    <>
      <PageHeader
        title={pageTitle}
        description={pageDescription}
        breadcrumbs={breadcrumbs}
        actions={
          canCreate ? (
            <>
              <Button onClick={handleNewAsset}>
                <Plus className="mr-2 h-4 w-4" />
                New Asset
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleFileChange}
              />
            </>
          ) : undefined
        }
      />

      <main className="flex-1 overflow-y-auto bg-muted/40">
        <div className="container mx-auto p-6 lg:p-8">
          <div className="flex flex-col gap-6">
            {showStats && <AssetStatCards />}

            <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
              <div className="hidden lg:block">
                <div className="sticky top-6">
                  <AssetFilters filters={filter} onFiltersChange={handleFiltersChange} />
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    {isLoading
                      ? 'Loading assets...'
                      : `${data?.total || 0} asset${data?.total !== 1 ? 's' : ''}`}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                    className="lg:hidden"
                  >
                    <Filter className="mr-2 h-4 w-4" />
                    Filters
                  </Button>
                </div>

                {showFilters && (
                  <div className="lg:hidden">
                    <AssetFilters filters={filter} onFiltersChange={handleFiltersChange} />
                  </div>
                )}

                <AssetTable
                  assets={data?.assets || []}
                  presence={presence}
                  isLoading={isLoading}
                  onEdit={setEditAssetId}
                  onDelete={setDeleteAssetId}
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      <AssetFormDialog
        mode="edit"
        asset={editRow as any}
        open={!!editAssetId}
        onOpenChange={(open) => !open && setEditAssetId(null)}
      />

      <AssetDeleteDialog
        asset={deleteRow as any}
        open={!!deleteAssetId}
        onOpenChange={(open) => !open && setDeleteAssetId(null)}
      />
    </>
  );
}
